import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ListResponseContract, Questionnaire, QuestionnaireContract } from '@isophi/teachers-shared'
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { TimestampObservableCache } from '../utils/timestamp-observable-cache';

@Injectable({
  providedIn: 'root'
})
export class QuestionnaireService {
  protected static readonly CACHE_EXPIRATION: number = 60; // in seconds

  protected questionnairesCache: TimestampObservableCache<Questionnaire[]> | null = null;

  protected questionnaireCache: { [key: string]: TimestampObservableCache<Questionnaire> } = {};

  public constructor(private http: HttpClient) {}

  public get questionnaires$(): Observable<Questionnaire[]> {
    const url = `${environment.teacherAPI}/questionnaire/`;

    if (this.questionnairesCache === null || this.questionnairesCache.isExpired()) {
      const questionnaires$ = this.http.get<ListResponseContract<QuestionnaireContract>>(url).pipe(
        map((response) => response.results.map((questionnaireData) => Questionnaire.deserialize(questionnaireData))),
        shareReplay(1),
        catchError((error) => {
          this.questionnairesCache = null;
          return throwError(() => error);
        })
      );

      this.questionnairesCache = new TimestampObservableCache<Questionnaire[]>(questionnaires$, QuestionnaireService.CACHE_EXPIRATION);
    }

    return this.questionnairesCache.observable;
  }

  public getQuestionnaire(uuid: string): Observable<Questionnaire> {
    const url = `${environment.teacherAPI}/questionnaire/${uuid}/`;

    const questionnaireCache = this.questionnaireCache[uuid];
    if (questionnaireCache === undefined || questionnaireCache.isExpired()) {
      const questionnaire$ = this.http.get<QuestionnaireContract>(url).pipe(
        map((questionnaireData) => Questionnaire.deserialize(questionnaireData)),
        shareReplay(1),
        catchError((error) => {
          delete this.questionnaireCache[uuid];
          return throwError(() => error);
        })
      );
      this.questionnaireCache[uuid] = new TimestampObservableCache<Questionnaire>(questionnaire$, QuestionnaireService.CACHE_EXPIRATION);
    }

    return this.questionnaireCache[uuid].observable;
  }
}
